import { z } from 'zod';

import { isNavigationDominant } from './source-navigation';

/** Editorial judgment is advisory. It cannot clear a factual or release gate. */
export const ARTICLE_CRITIQUE_RUBRIC = 'article-editorial-v1';
/**
 * The version of everything that makes a stored critique mean what it says besides the rubric's name: the
 * review prompt, the validator's rules and the window of source text the model is shown. A stored critique
 * is reused for an unchanged article only while its rubric AND this number are the current ones, so a change
 * to either is the one way, besides an edit or an explicit refresh, that a review is run again. A record
 * written before the number existed is version 1.
 */
export const ARTICLE_CRITIQUE_VERSION = 1;
export const ARTICLE_CRITIQUE_DIMENSIONS = [
  'evidence',
  'brand_voice',
  'reader_value',
  'originality',
  'structure',
  'prose',
] as const;

const ARTICLE_CRITIQUE_DIMENSION_LABELS: Record<
  (typeof ARTICLE_CRITIQUE_DIMENSIONS)[number],
  string
> = {
  evidence: 'Evidence',
  brand_voice: 'Brand voice',
  reader_value: 'Reader value',
  originality: 'Originality',
  structure: 'Structure',
  prose: 'Prose',
};

/** Human label for a critique dimension id; an unknown id stays identifiable. */
export function critiqueDimensionLabel(id: string): string {
  return (
    ARTICLE_CRITIQUE_DIMENSION_LABELS[id as (typeof ARTICLE_CRITIQUE_DIMENSIONS)[number]] ??
    id.replace(/_/g, ' ')
  );
}

export const ArticleCritiqueDraftSchema = z.object({
  summary: z.string().describe('A candid editorial assessment, not a publication clearance.'),
  strengths: z.array(z.string()),
  dimensions: z.array(
    z.object({
      dimension: z.enum(ARTICLE_CRITIQUE_DIMENSIONS),
      assessment: z.enum(['strong', 'needs_attention', 'not_assessed']),
      reason: z.string(),
    }),
  ),
  findings: z.array(
    z.object({
      dimension: z.enum(ARTICLE_CRITIQUE_DIMENSIONS),
      basis: z.enum(['editorial_judgment', 'unsupported_claim', 'source_conflict']),
      quote: z.string().describe('Exact contiguous text from the article being critiqued.'),
      reason: z
        .string()
        .describe('What this passage does poorly for this reader, with specific reasoning.'),
      suggestion: z
        .string()
        .describe('A concrete edit or evidence needed. Never invent a replacement fact.'),
      evidence: z.array(
        z.object({
          sourceId: z.string(),
          quote: z.string().describe('Exact contiguous text from the supplied source excerpt.'),
        }),
      ),
    }),
  ),
  limitations: z
    .array(z.string())
    .describe('Missing evidence, voice context, or aspects you could not assess.'),
});

export type ArticleCritiqueDraft = z.infer<typeof ArticleCritiqueDraftSchema>;
export interface ArticleCritiqueSource {
  sourceId: string;
  url: string;
  text: string;
  /** Retrieval is a receipt, not proof that a source is correct or authoritative. */
  evidenceKind: 'page_text' | 'model_transcript';
  truncated: boolean;
}

export type CritiqueFailureCode =
  | 'timeout'
  | 'cancelled'
  | 'input_too_large'
  | 'no_output'
  | 'schema_invalid'
  | 'provider_error'
  | 'provider_or_schema_error';

const FAILURE_WORDS: Record<CritiqueFailureCode, string> = {
  timeout: 'the review took longer than its time limit',
  cancelled: 'the job was cancelled',
  input_too_large: 'the article and its sources were larger than one review can read',
  no_output: 'the model returned no usable answer (often an answer cut off at its length limit)',
  schema_invalid: "the model's answer did not match the review's format",
  provider_error: 'the model provider returned an error',
  provider_or_schema_error: 'the model provider or its answer failed',
};

/**
 * What went wrong, as a stable code and a short redacted detail. Nothing is raised for the operator when a
 * critique cannot finish except this: before it, two topics' rechecks ended `partial` with no code and no
 * message and the reason could be found only in a server log.
 */
export function critiqueFailure(
  error: unknown,
  context: { cancelled: boolean; timedOut: boolean },
): { code: CritiqueFailureCode; detail: string } {
  const named = (error && typeof error === 'object' ? error : {}) as {
    name?: unknown;
    message?: unknown;
    statusCode?: unknown;
    issues?: unknown;
    cause?: unknown;
  };
  const name = typeof named.name === 'string' ? named.name : 'Error';
  const message = typeof named.message === 'string' ? named.message : '';
  const redact = (text: string) =>
    text
      .replace(/https?:\/\/\S+/g, '[url]')
      .replace(/\b(?:key|token|authorization|bearer)\b[=:\s]+\S+/gi, '$1 [redacted]')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 240);
  const issues = Array.isArray(named.issues)
    ? named.issues
    : Array.isArray((named.cause as { issues?: unknown } | undefined)?.issues)
      ? ((named.cause as { issues: unknown[] }).issues as unknown[])
      : null;
  if (context.cancelled) return { code: 'cancelled', detail: FAILURE_WORDS.cancelled };
  if (context.timedOut) return { code: 'timeout', detail: FAILURE_WORDS.timeout };
  if (/exceeds bounded review size/i.test(message))
    return { code: 'input_too_large', detail: redact(message) };
  if (name === 'ZodError' || (issues && name !== 'AI_APICallError'))
    return {
      code: 'schema_invalid',
      detail: redact(
        (issues ?? [])
          .slice(0, 4)
          .map((issue) => {
            const entry = issue as { path?: unknown[]; message?: string };
            return `${(entry.path ?? []).join('.') || '(root)'}: ${entry.message ?? ''}`;
          })
          .join('; ') || message,
      ),
    };
  if (/^AI_NoOutputGeneratedError$|^AI_NoObjectGeneratedError$/.test(name))
    return { code: 'no_output', detail: redact(`${name}${message ? `: ${message}` : ''}`) };
  const status = typeof named.statusCode === 'number' ? ` (HTTP ${named.statusCode})` : '';
  return {
    code: 'provider_error',
    detail: redact(`${name}${status}${message ? `: ${message}` : ''}`),
  };
}

export function critiqueFailureWords(code: CritiqueFailureCode): string {
  return FAILURE_WORDS[code];
}

/**
 * What the critique of an address-edited version records about how it was made (`article-address-scope.ts`): the
 * findings on untouched passages were carried from the earlier critique and only the edited passages were reviewed.
 */
export interface ScopedReviewRecord {
  version: 1;
  /** The version whose critique the carried findings come from. */
  baseContentHash: string;
  editedPassages: number;
  /** Earlier findings on untouched passages, carried as they were. */
  carried: number;
  /** Earlier findings on an edited passage that the edit resolved. */
  closed: number;
  /** Carried findings, plus earlier findings an edit did not resolve. */
  stillOpen: number;
  /** Findings the edits introduced, all of them inside an edited passage. */
  new: number;
  /** The keys of the new findings, so the panel can tell them from the ones that were already open. */
  newKeys: string[];
  passages: { id: string; resolved: boolean; reason: string }[];
  outsideEdits: number;
}

export interface ArticleCritiqueReport extends ArticleCritiqueDraft {
  rubric: typeof ARTICLE_CRITIQUE_RUBRIC;
  status: 'reviewed' | 'incomplete' | 'unavailable';
  contentHash: string;
  contextHash: string;
  checkedAt: string;
  modelId: string;
  rejectedFindings: number;
  /** See `ARTICLE_CRITIQUE_VERSION`. Absent on a record written before the number existed (version 1). */
  critiqueVersion?: number;
  /**
   * Set when this record is an earlier critique of the same article text, carried to a later recheck instead of
   * being run again (`decideCritiqueReuse`). `checkedAt`, `contextHash` and everything the model said are the
   * original run's; this is when it was carried.
   */
  reusedAt?: string;
  /** Present when only the passages an address pass edited were reviewed (see `ScopedReviewRecord`). */
  scopedReview?: ScopedReviewRecord;
  /**
   * Why a critique that did not run to the end is `unavailable`. `provider_or_schema_error` is the code older
   * records carry; a new record names the cause (`critiqueFailure`).
   */
  failureReason?: CritiqueFailureCode;
  /** One short, redacted sentence of the underlying error, for a person deciding whether to retry. */
  failureDetail?: string;
  usage?: {
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    durationMs: number;
  };
}

const WHITESPACE = /[\u00A0\u2000-\u200B\u202F\s]/;
const LIST_MARKER = /^\s*(?:[-*+]|\d+[.)])\s+/;
const TABLE_RULE = /^\s*\|?[\s:|-]+\|?\s*$/;
const MARKDOWN_LINK = /(!?)\[([^\]]*)\]\(([^)]*)\)/g;

interface Projection {
  /** The visible words: markup, link targets and line structure taken out, whitespace collapsed. */
  plain: string;
  /** For each character of `plain`, where it sits in the original text. */
  origin: number[];
  /** For each character of `plain`, the link whose label carries it (original start and end), or null. */
  link: Array<[number, number] | null>;
}

/**
 * The words a reader sees. Markup is not content: link syntax (`[words](https://...)` is "words"), an image,
 * emphasis marks, table pipes and rules, heading and quote marks and list markers are dropped, whitespace
 * collapses to one space and curly quotes become straight. Nothing else changes: never case, never a word.
 * Each visible character keeps the position it came from, so a passage found in the projection can be cut
 * out of the original.
 */
function project(text: string): Projection {
  const hidden = new Uint8Array(text.length);
  const linkOf: Array<[number, number] | null> = new Array(text.length).fill(null);
  for (const match of text.matchAll(MARKDOWN_LINK)) {
    const from = match.index!;
    const to = from + match[0].length;
    const labelFrom = from + match[1]!.length + 1;
    const labelTo = labelFrom + match[2]!.length;
    for (let i = from; i < to; i += 1) {
      if (match[1] || i < labelFrom || i >= labelTo) hidden[i] = 1;
      else linkOf[i] = [from, to];
    }
  }
  let lineStart = 0;
  for (const line of text.split('\n')) {
    const lineEnd = lineStart + line.length;
    const marker = LIST_MARKER.exec(line);
    if (TABLE_RULE.test(line) && line.includes('-')) {
      for (let i = lineStart; i < lineEnd; i += 1) hidden[i] = 1;
    } else if (marker) {
      for (let i = lineStart; i < lineStart + marker[0].length; i += 1) hidden[i] = 1;
    }
    lineStart = lineEnd + 1;
  }
  const plain: string[] = [];
  const origin: number[] = [];
  const link: Array<[number, number] | null> = [];
  const push = (char: string, at: number) => {
    plain.push(char);
    origin.push(at);
    link.push(linkOf[at] ?? null);
  };
  for (let i = 0; i < text.length; i += 1) {
    if (hidden[i]) continue;
    const char = text[i]!;
    if (/[`*_~]/.test(char)) continue;
    if (WHITESPACE.test(char) || char === '|' || char === '>' || char === '#') {
      if (plain.length && plain[plain.length - 1] !== ' ') push(' ', i);
      continue;
    }
    if (char === '\u2018' || char === '\u2019') push("'", i);
    else if (char === '\u201C' || char === '\u201D') push('"', i);
    else push(char, i);
  }
  while (plain.length && plain[plain.length - 1] === ' ') {
    plain.pop();
    origin.pop();
    link.pop();
  }
  return { plain: plain.join(''), origin, link };
}

/** A quote with an ellipsis is the pieces it quotes, in order. */
const ELLIPSIS = /\[?\s*(?:\u2026|\.{3,})\s*\]?/;
/** A piece shorter than this carries no weight: "the" matches anywhere. */
const MIN_PIECE_CHARS = 8;
const MIN_QUOTE_CHARS = 12;
/** An elided quote that spans more than this of the article is not one passage. */
const MAX_ELIDED_SPAN_CHARS = 2000;

function quotePieces(quote: string): string[] {
  const pieces = quote
    .split(ELLIPSIS)
    .map((piece) => project(piece).plain)
    .filter(Boolean);
  const weighty = pieces.filter((piece) => piece.length >= MIN_PIECE_CHARS);
  return weighty.length ? weighty : pieces;
}

/**
 * Where each piece of a quote lies in the visible text, in order: the first occurrence of the first piece
 * that lets every later piece follow it. Null when a piece is not there.
 */
function findPieces(
  plain: string,
  pieces: readonly string[],
): { start: number; end: number } | null {
  if (!pieces.length || pieces.join('').length < MIN_QUOTE_CHARS) return null;
  let from = plain.indexOf(pieces[0]!);
  for (let attempts = 0; from >= 0 && attempts < 25; attempts += 1) {
    let cursor = from + pieces[0]!.length;
    let ok = true;
    for (const piece of pieces.slice(1)) {
      const at = plain.indexOf(piece, cursor);
      if (at < 0) {
        ok = false;
        break;
      }
      cursor = at + piece.length;
    }
    if (ok) return { start: from, end: cursor };
    from = plain.indexOf(pieces[0]!, from + 1);
  }
  return null;
}

/**
 * A model quote counts as found when it is a contiguous passage of the supplied source, or the passages
 * an ellipsis joins, in order. Line breaks, curly quotes and markup are not content: a quote copied from a table
 * row, a wrapped paragraph or a page whose links the model read as plain words used to be
 * rejected for them, and one rejected quote made the whole review `incomplete`.
 */
export function quoteIsInSource(quote: string, sourceText: string): boolean {
  if (sourceText.includes(quote)) return true;
  return findPieces(project(sourceText).plain, quotePieces(quote)) !== null;
}

/**
 * The exact passage of the article a model's quote points at, or null. The model is asked for exact
 * contiguous text and returns it with a line break turned into a space, a curly quote straightened, the
 * emphasis marks of a bold phrase dropped, and, most often, a link read as plain words
 * ("Under § 403.605, that taxable value" for `Under [§ 403.605](https://...), that taxable value`). It may
 * also join two passages with an ellipsis. That is the same passage: it is found, and the finding is kept with
 * the article's own text as its quote, so everything that locates it later (the editor's highlight, a
 * decision keyed by the quote) still works on exact text. A word added, changed or reordered is not found.
 */
export function locateQuoteInArticle(markdown: string, quote: string): string | null {
  if (!quote.trim()) return null;
  if (markdown.includes(quote)) return quote;
  if (quote.length > 3000) return null;
  const projection = project(markdown);
  const found = findPieces(projection.plain, quotePieces(quote));
  if (!found) return null;
  const first = found.start;
  const last = found.end - 1;
  // A passage that starts or ends inside a link's label takes the whole link, so the cut text is not
  // half a `[label](url)`.
  const from = projection.link[first]?.[0] ?? projection.origin[first]!;
  const to = projection.link[last]?.[1] ?? projection.origin[last]! + 1;
  if (to - from > Math.max(MAX_ELIDED_SPAN_CHARS, quote.length * 3)) return null;
  const located = markdown.slice(from, to).trim();
  return located.length >= MIN_QUOTE_CHARS ? located : null;
}

/**
 * The model's own limitation says the pages it was given were navigation, not
 * source content ("the supplied excerpts are navigation menus only"). Such a review
 * cannot have assessed evidence, whatever else it returned.
 */
const NAVIGATION_ONLY_LIMITATION =
  /\bnavigation(?:al)?[- ](?:only|menus?|links?|text|boilerplate|pages?)\b|\b(?:only|just|mostly|primarily|merely)\b[^.]{0,60}\bnavigation(?:al)?\b|\bnavigation(?:al)?\b[^.]{0,60}\b(?:only|no substantive|rather than)\b/i;

const NAMES_A_SOURCE = /\b(?:sources?|excerpts?|pages?|retrieved|supplied|provided)\b/i;

export function limitationSaysSourcesWereNavigation(limitations: readonly string[]): boolean {
  return limitations.some(
    (limitation) => NAMES_A_SOURCE.test(limitation) && NAVIGATION_ONLY_LIMITATION.test(limitation),
  );
}

type CritiqueFinding = ArticleCritiqueDraft['findings'][number];

const head = (text: string, max = 160) =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

/**
 * A sentence that points to ANOTHER PIECE of the same programme ("Our piece Powered Shell or Turnkey? covers how
 * ...", "When Is a Second Opinion on Property Tax Worth It? covers the earlier question ..."). Its evidence is
 * the programme's own library, which is never one of the sources supplied to a review, so the model names it
 * `workspace`. That is a link to a sibling article, not a claim about a source, and listing it as "names a
 * source that was not supplied" was noise on four articles of the v4 run.
 */
const OUR_PIECE = /\bour\s+(?:piece|article|post|guide|explainer)\b/i;
const POINTS_TO_A_TITLE =
  /^(.{8,160}?)\s(?:covers|explains|sets out|looks at|follows|walks through|discusses|goes through|details|describes)\b/;

export function isSiblingArticlePointer(quote: string): boolean {
  const text = quote
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (OUR_PIECE.test(text)) return true;
  // A sentence led by a Title Case headline, then a verb that says what that piece covers.
  const lead = POINTS_TO_A_TITLE.exec(text)?.[1];
  return Boolean(lead && (lead.match(/\b[A-Z][A-Za-z'\u2019-]+/g) ?? []).length >= 3);
}

/**
 * One finding, accepted with its quote resolved to the article's own text, or rejected with the reason a
 * person can read. The reason is kept on the report: a rejection used to leave only a count, so the 15
 * `incomplete` reviews of 2026-10-05 could not be told apart.
 */
function checkFinding(
  finding: CritiqueFinding,
  markdown: string,
  sources: readonly ArticleCritiqueSource[],
): {
  source: CritiqueFinding;
  accepted: CritiqueFinding | null;
  why: string | null;
  /** Dropped without comment: not a finding about a source at all. */
  ignored?: boolean;
} {
  const reject = (why: string) => ({ source: finding, accepted: null, why });
  if (!finding.quote.trim()) return reject('a finding with no quotation');
  const quote = locateQuoteInArticle(markdown, finding.quote);
  if (!quote) return reject(`the article text “${head(finding.quote)}” is not in the article.`);
  if (!finding.reason.trim() || !finding.suggestion.trim())
    return reject(`the finding on “${head(quote)}” has no reason or no suggestion.`);
  if (finding.basis === 'source_conflict' && finding.evidence.length === 0)
    return reject(`the source conflict on “${head(quote)}” names no source passage.`);
  for (const reference of finding.evidence) {
    if (!reference.quote.trim())
      return reject(`an evidence quotation on “${head(quote)}” is empty.`);
    const source = sources.find((candidate) => candidate.sourceId === reference.sourceId);
    if (!source && isSiblingArticlePointer(quote))
      return { source: finding, accepted: null, why: null, ignored: true };
    if (!source)
      return reject(
        `the evidence for “${head(quote)}” names a source that was not supplied (${reference.sourceId}).`,
      );
    if (!quoteIsInSource(reference.quote, source.text))
      return reject(
        `the passage “${head(reference.quote)}” is not in the text shown for source ${reference.sourceId}.`,
      );
  }
  return { source: finding, accepted: { ...finding, quote }, why: null };
}

/**
 * One dimension's reason when findings are what make it need attention. The model's own reason is kept only
 * when the model itself rated the dimension `needs_attention`: a model that wrote "all citations match the
 * sources" and rated it `strong` has not described what the quoted findings say, and the dimension used to
 * read "needs attention" beside a sentence of praise (topics 401a57af and 0a521710 of 2026-10-05).
 */
function reasonFromFindings(findings: readonly CritiqueFinding[]): string {
  const first = head(findings[0]!.reason.replace(/\s+/g, ' ').trim(), 200);
  return findings.length === 1
    ? `One passage needs attention: ${first}`
    : `${findings.length} passages need attention, starting with: ${first}`;
}

/**
 * Validate model references against the exact supplied artifact/evidence.
 * A plausible quote or invented source id must never become a credible-looking
 * finding, so a finding whose quotation cannot be found is DROPPED and the drop is written into the
 * limitations. It no longer marks its whole dimension `not_assessed`: that left 15 of 27 reviews of 2026-10-05
 * `incomplete` for one unfindable quote, with every other finding of the dimension thrown away.
 *
 * A dimension's assessment follows from the findings that survive, not from the model's label: any finding
 * means `needs_attention`, none means `strong`, so the two can never contradict.
 */
export function validateArticleCritique(
  draft: ArticleCritiqueDraft,
  markdown: string,
  sources: readonly ArticleCritiqueSource[],
  context: { hasBrandVoice: boolean } = { hasBrandVoice: false },
): Pick<
  ArticleCritiqueReport,
  'status' | 'findings' | 'dimensions' | 'limitations' | 'rejectedFindings'
> {
  const allChecks = draft.findings.map((finding) => checkFinding(finding, markdown, sources));
  // A finding about a pointer to a sibling article is not a finding and not a rejection.
  const checks = allChecks.filter((check) => !check.ignored);
  const findings = checks.flatMap((check) => (check.accepted ? [check.accepted] : []));
  const rejectedFindings = checks.length - findings.length;
  const pages = sources.filter((source) => source.evidenceKind === 'page_text');
  const navigationOnly =
    (pages.length > 0 && pages.every((source) => isNavigationDominant(source.text))) ||
    limitationSaysSourcesWereNavigation(draft.limitations);
  const flaggedWithoutFinding: string[] = [];
  const dimensions = ARTICLE_CRITIQUE_DIMENSIONS.map((dimension) => {
    if (dimension === 'brand_voice' && !context.hasBrandVoice) {
      return {
        dimension,
        assessment: 'not_assessed' as const,
        reason: 'No configured workspace voice was supplied.',
      };
    }
    if (
      dimension === 'evidence' &&
      !sources.some((source) => source.evidenceKind === 'page_text')
    ) {
      return {
        dimension,
        assessment: 'not_assessed' as const,
        reason: 'No retrieved page text was supplied to check factual support.',
      };
    }
    if (dimension === 'evidence' && navigationOnly) {
      return {
        dimension,
        assessment: 'not_assessed' as const,
        reason:
          'The retrieved pages were navigation menus and link lists, not the sources\u2019 own text, so factual support could not be checked.',
      };
    }
    const matches = draft.dimensions.filter((entry) => entry.dimension === dimension);
    const own = findings.filter((finding) => finding.dimension === dimension);
    if (own.length) {
      const modelsOwn = matches[0];
      return {
        dimension,
        assessment: 'needs_attention' as const,
        reason:
          modelsOwn?.assessment === 'needs_attention' && modelsOwn.reason.trim()
            ? modelsOwn.reason
            : reasonFromFindings(own),
      };
    }
    const entry = matches.length === 1 ? matches[0] : undefined;
    if (!entry || !entry.reason.trim()) {
      return {
        dimension,
        assessment: 'not_assessed' as const,
        reason: 'No unambiguous assessment was returned.',
      };
    }
    if (entry.assessment === 'not_assessed') return entry;
    const dropped = checks.filter(
      (check) => !check.accepted && check.source.dimension === dimension,
    ).length;
    if (entry.assessment === 'strong' && !dropped) return entry;
    if (entry.assessment === 'needs_attention') flaggedWithoutFinding.push(dimension);
    return {
      dimension,
      assessment: 'strong' as const,
      reason: dropped
        ? `No verified finding is open. ${dropped === 1 ? 'A finding' : `${dropped} findings`} for this dimension could not be tied to the article's text and ${dropped === 1 ? 'was' : 'were'} dropped (see the limitations).`
        : 'No passage was quoted for this dimension, so no finding is open.',
    };
  });
  const limitations = [...draft.limitations];
  limitations.push(
    'Originality describes distinctiveness within the supplied material, not a plagiarism search or a comparison with the whole web.',
  );
  if (rejectedFindings) {
    limitations.push(
      `${rejectedFindings} finding(s) were dropped because their quotations or evidence references could not be verified.`,
    );
    for (const check of checks.filter((entry) => !entry.accepted).slice(0, 6))
      limitations.push(`Rejected: ${check.why}`);
  }
  for (const dimension of flaggedWithoutFinding)
    limitations.push(
      `The reviewer rated ${dimension.replace('_', ' ')} as needing attention without quoting a passage, so no finding is open for it.`,
    );
  if (!sources.length)
    limitations.push(
      'No retrieved source excerpts were supplied. This review cannot establish factual support.',
    );
  if (navigationOnly)
    limitations.push(
      'The retrieved pages were navigation, not source content. Evidence was not assessed.',
    );
  if (sources.some((source) => source.truncated))
    limitations.push('Some source excerpts are truncated; omitted context was not assessed.');
  if (sources.some((source) => source.evidenceKind === 'model_transcript'))
    limitations.push(
      'Generated transcripts are secondary interpretations, not verbatim source evidence.',
    );
  return {
    status: dimensions.some((entry) => entry.assessment === 'not_assessed')
      ? 'incomplete'
      : 'reviewed',
    findings,
    dimensions,
    limitations: [...new Set(limitations)],
    rejectedFindings,
  };
}
